// Footer chung cho các trang công khai (trang chủ, chủ đề, vận khí).
// Tách ra từ trang chủ cũ để nhiều trang dùng lại, không lặp lại markup.
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Facebook,
  Instagram,
  PackageOpen,
  Sparkles,
  Users,
  Youtube,
} from "lucide-react";
import logoIcon96 from "@/assets/logo-icon-96.webp";
import { BusinessInfoBlock } from "@/components/business-info-block";

const INFO_LINKS = [
  { label: "Điều khoản dịch vụ", to: "/terms" },
  { label: "Chính sách bảo mật", to: "/privacy" },
  { label: "Bản quyền", to: "/copyright" },
  { label: "Hướng dẫn sử dụng", to: "/user-manual" },
  { label: "Câu hỏi thường gặp", to: "/faq" },
] as const;

const FACEBOOK_GROUP_URL = "https://www.facebook.com/groups/232657737355718";

const SOCIAL_LINKS = [
  { label: "TikTok Bói Đê", href: "https://www.tiktok.com/@boide.online", icon: <TikTokIcon /> },
  {
    label: "YouTube Bói Đê",
    href: "https://www.youtube.com/@boideonline",
    icon: <Youtube className="h-4 w-4" />,
  },
  { label: "Threads Bói Đê", href: "https://www.threads.com/@boide.online", icon: <ThreadsIcon /> },
  {
    label: "Instagram Bói Đê",
    href: "https://www.instagram.com/boide.online",
    icon: <Instagram className="h-4 w-4" />,
  },
  {
    label: "Fanpage Facebook Bói Đê",
    href: "https://www.facebook.com/profile.php?id=61591569002475",
    icon: <Facebook className="h-4 w-4" />,
  },
] as const;

export function SiteFooter({
  showOtherProducts = true,
  boxed = false,
}: {
  showOtherProducts?: boolean;
  /**
   * Chân trang được đặt TRONG một tấm panel (giao diện sự kiện) — bỏ đường kẻ trên
   * và lề trên của chính nó, để panel làm khung.
   *
   * ⚠️ Không tự suy bằng `useEventUI()`: chân trang này còn dùng ở `/van-khi` và
   * `/chu-de`, nơi KHÔNG có artwork nào phía sau. Dựng panel mờ ở đó là một tấm kính
   * đặt trên nền trơn — thấy rõ mà chẳng che cái gì.
   */
  boxed?: boolean;
} = {}) {
  return (
    <footer className={boxed ? "" : "mt-12 border-t border-gold-soft/20 pt-8"}>
      <div
        className={`grid grid-cols-1 gap-8 sm:grid-cols-2 ${
          showOtherProducts ? "lg:grid-cols-3" : ""
        }`}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <img
              src={logoIcon96}
              alt="Bói Đê"
              width={44}
              height={44}
              className="h-11 w-11 shrink-0 object-contain drop-shadow-[0_0_8px_rgba(229,184,105,0.35)]"
            />
            <div>
              <p className="font-serif-soft text-xl leading-none text-gold-gradient">Bói Đê</p>
              <p className="mt-1 text-[11px] italic text-primary/70">
                Gieo quẻ soi lối, Bói Đê dẫn đường
              </p>
            </div>
          </div>
          <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
            Bói Đê đồng hành cùng bạn khám phá vận mệnh — gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát
            Tự, luận giải bằng AI chuẩn xác.
          </p>
          <div className="flex flex-wrap gap-2">
            {SOCIAL_LINKS.map((s) => (
              <SocialIcon key={s.label} href={s.href} label={s.label}>
                {s.icon}
              </SocialIcon>
            ))}
            <SocialIcon href={FACEBOOK_GROUP_URL} label="Nhóm Facebook Bói Đê">
              <Users className="h-4 w-4" />
            </SocialIcon>
          </div>
        </div>

        <BusinessInfoBlock />

        {showOtherProducts ? (
          <div className="space-y-2.5 text-sm">
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
              <PackageOpen className="h-3.5 w-3.5" /> Sản phẩm khác
            </p>
            <a
              href="https://uni.ai.vn"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-1.5 font-medium text-foreground transition hover:text-primary"
            >
              <Sparkles className="h-4 w-4 text-primary/70" /> uni.ai.vn
              <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
              Nền tảng và hệ sinh thái giải pháp AI thế hệ mới.
            </p>
          </div>
        ) : null}
      </div>

      <div className="mt-8 flex flex-col items-center gap-3 border-t border-gold-soft/10 pt-5 sm:flex-row sm:justify-between">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
          {INFO_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="transition hover:text-primary">
              {link.label}
            </Link>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">© 2026 Bói Đê</p>
      </div>
    </footer>
  );
}

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  const isExternal = href.startsWith("http");
  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer" : undefined}
      aria-label={label}
      title={label}
      className="group flex h-10 w-10 items-center justify-center rounded-xl border border-gold-soft/20 bg-background/25 text-muted-foreground transition-all duration-300 hover:border-primary/50 hover:bg-background/40 hover:text-primary md:hover:-translate-y-0.5"
    >
      {children}
    </a>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M12.8 2h3.1c.2 1.6 1.1 3 2.4 3.9.7.5 1.6.7 2.5.8v3.2c-1.7 0-3.3-.5-4.6-1.4v6.4c0 3.4-2.8 6.2-6.2 6.1-3.1-.1-5.6-2.6-5.7-5.7-.1-3.4 2.6-6.1 6-6.1.3 0 .6 0 .9.1v3.3c-.3-.1-.6-.2-1-.2-1.5 0-2.7 1.3-2.6 2.8.1 1.4 1.3 2.5 2.7 2.5 1.5 0 2.8-1.2 2.8-2.7V2z" />
    </svg>
  );
}

function ThreadsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M12.2 22C6.7 22 3 18 3 12S6.7 2 12.2 2c3.9 0 6.8 1.7 8.3 4.9l-1.9.9c-1.1-2.3-3.1-3.6-6.4-3.6C8 4.2 5.1 7 5.1 12s2.9 7.8 7.1 7.8c2.9 0 4.8-1.2 5.6-3.1.5-1.1.4-2.2-.1-3-.5-.9-1.5-1.5-2.8-1.8 0 1.6-.4 2.8-1.2 3.6-.7.7-1.6 1-2.8 1-1.1 0-2-.4-2.6-1-.7-.7-.9-1.6-.7-2.5.3-1.7 1.9-2.7 4.1-2.7.6 0 1.1 0 1.6.1 0-1.4-.8-2.2-2.1-2.2-1 0-1.8.4-2.3 1.2l-1.6-1.1c.9-1.3 2.3-2 4-2 2.6 0 4.1 1.7 4.1 4.4v.5c1.6.5 2.8 1.5 3.4 2.9.7 1.5.6 3.5-.5 5.2C18.6 20.5 15.8 22 12.2 22zm.4-8.6c-1.3 0-2.2.5-2.3 1.3-.1.7.5 1.1 1.4 1.1.7 0 1.2-.2 1.6-.6.4-.5.6-1.2.6-2.1-.5-.1-.9-.1-1.3-.1z" />
    </svg>
  );
}
